/**
 * Cookie & Storage Management Utility
 * Provides consent-aware storage operations with expiration support
 */

export type ConsentCategory = 'essential' | 'analytics' | 'marketing' | 'preferences';

export interface CookieConsentState {
  essential: boolean; // Always true - required for site to function
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  timestamp: number;
}

const CONSENT_KEY = 'cookie-consent';
const CONSENT_VERSION = '1.0';

// Default consent state (only essential enabled)
const DEFAULT_CONSENT: CookieConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
  timestamp: 0,
};

/**
 * Get current consent state from localStorage
 */
export function getConsentState(): CookieConsentState | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    // Validate structure
    if (typeof parsed.essential !== 'boolean') return null;
    
    return parsed as CookieConsentState;
  } catch {
    return null;
  }
}

/**
 * Save consent state to localStorage
 */
export function saveConsentState(consent: Partial<CookieConsentState>): void {
  const state: CookieConsentState = {
    ...DEFAULT_CONSENT,
    ...consent,
    essential: true, // Always enforce essential
    timestamp: Date.now(),
  };
  
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to save consent state:', e);
  }
}

/**
 * Check if user has made a consent decision
 */
export function hasConsentDecision(): boolean {
  return getConsentState() !== null;
}

/**
 * Check if a specific category is consented
 */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === 'essential') return true;
  
  const state = getConsentState();
  if (!state) return false;
  
  return state[category] === true;
}

/**
 * Accept all cookies
 */
export function acceptAllCookies(): void {
  saveConsentState({
    essential: true,
    analytics: true,
    marketing: true,
    preferences: true,
  });
}

/**
 * Accept only essential cookies
 */
export function acceptEssentialOnly(): void {
  saveConsentState({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
}

/**
 * Consent-aware localStorage wrapper
 */
export const storage = {
  /**
   * Get item from localStorage (always allowed for essential)
   */
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  /**
   * Set item in localStorage with optional category check
   */
  set(key: string, value: string, category: ConsentCategory = 'essential'): boolean {
    if (!hasConsent(category)) {
      return false;
    }
    
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Remove item from localStorage
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Silent fail
    }
  },

  /**
   * Get item from sessionStorage
   */
  getSession(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },

  /**
   * Set item in sessionStorage with optional category check
   */
  setSession(key: string, value: string, category: ConsentCategory = 'essential'): boolean {
    if (!hasConsent(category)) {
      return false;
    }
    
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Remove item from sessionStorage
   */
  removeSession(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Silent fail
    }
  },
};

/**
 * Clear non-essential storage when consent is revoked
 */
export function clearNonEssentialStorage(): void {
  const essentialKeys = [
    CONSENT_KEY,
    'supabase.auth.token',
    'sb-yhrjfxmxceuqeadivfvv-auth-token', // Supabase auth
  ];

  // Clear localStorage except essential
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !essentialKeys.some(essential => key.includes(essential))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Silent fail
  }

  // Clear sessionStorage except essential
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && !essentialKeys.some(essential => key.includes(essential))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch {
    // Silent fail
  }
}
